import React from 'react';
import { Modal, Button, Space, Typography, DatePicker, Divider, Drawer } from 'antd';
import { FilterOutlined, ClearOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import useIsMobile from '../../hooks/useIsMobile';

const { Text } = Typography;
const { RangePicker } = DatePicker;

// Date preset options
const DATE_PRESETS = [
  { key: 'all', label: 'All' },
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'this_week', label: 'This Week' },
  { key: 'last_week', label: 'Last Week' },
  { key: 'this_month', label: 'This Month' },
  { key: 'last_month', label: 'Last Month' },
  { key: 'last_30_days', label: 'Last 30 Days' },
  { key: 'last_60_days', label: 'Last 60 Days' },
  { key: 'last_90_days', label: 'Last 90 Days' },
  { key: 'this_quarter', label: 'This Quarter' },
  { key: 'last_quarter', label: 'Last Quarter' },
  { key: 'this_year', label: 'This Year' },
  { key: 'last_year', label: 'Last Year' },
  { key: 'this_fy', label: 'This FY' },
  { key: 'last_fy', label: 'Last FY' },
];

/**
 * Calculate date range based on preset
 * @param {string} preset - The preset key
 * @returns {[dayjs.Dayjs, dayjs.Dayjs] | null} - Date range array or null
 */
export const getDateRangeFromPreset = (preset) => {
  let start, end;

  switch (preset) {
    case 'all':
      return null;
    case 'today':
      start = dayjs().startOf('day');
      end = dayjs().endOf('day');
      break;
    case 'yesterday':
      start = dayjs().subtract(1, 'day').startOf('day');
      end = dayjs().subtract(1, 'day').endOf('day');
      break;
    case 'this_week':
      start = dayjs().startOf('week');
      end = dayjs().endOf('week');
      break;
    case 'last_week':
      start = dayjs().subtract(1, 'week').startOf('week');
      end = dayjs().subtract(1, 'week').endOf('week');
      break;
    case 'this_month':
      start = dayjs().startOf('month');
      end = dayjs().endOf('month');
      break;
    case 'last_month':
      start = dayjs().subtract(1, 'month').startOf('month');
      end = dayjs().subtract(1, 'month').endOf('month');
      break;
    case 'last_30_days':
      start = dayjs().subtract(30, 'days').startOf('day');
      end = dayjs().endOf('day');
      break;
    case 'last_60_days':
      start = dayjs().subtract(60, 'days').startOf('day');
      end = dayjs().endOf('day');
      break;
    case 'last_90_days':
      start = dayjs().subtract(90, 'days').startOf('day');
      end = dayjs().endOf('day');
      break;
    case 'this_quarter':
      start = dayjs().startOf('quarter');
      end = dayjs().endOf('quarter');
      break;
    case 'last_quarter':
      start = dayjs().subtract(1, 'quarter').startOf('quarter');
      end = dayjs().subtract(1, 'quarter').endOf('quarter');
      break;
    case 'this_year':
      start = dayjs().startOf('year');
      end = dayjs().endOf('year');
      break;
    case 'last_year':
      start = dayjs().subtract(1, 'year').startOf('year');
      end = dayjs().subtract(1, 'year').endOf('year');
      break;
    case 'this_fy':
      // Indian Fiscal Year: April 1 to March 31
      if (dayjs().month() >= 3) {
        start = dayjs().month(3).startOf('month');
        end = dayjs().add(1, 'year').month(2).endOf('month');
      } else {
        start = dayjs().subtract(1, 'year').month(3).startOf('month');
        end = dayjs().month(2).endOf('month');
      }
      break;
    case 'last_fy':
      if (dayjs().month() >= 3) {
        start = dayjs().subtract(1, 'year').month(3).startOf('month');
        end = dayjs().month(2).endOf('month');
      } else {
        start = dayjs().subtract(2, 'year').month(3).startOf('month');
        end = dayjs().subtract(1, 'year').month(2).endOf('month');
      }
      break;
    default:
      start = dayjs().subtract(30, 'days').startOf('day');
      end = dayjs().endOf('day');
  }

  return [start, end];
};

/**
 * ReportFilterModal - Modal for mobile filter display
 * @param {Object} props
 * @param {boolean} props.visible - Modal visibility
 * @param {Function} props.onClose - Close handler
 * @param {string} props.filterPreset - Current date preset
 * @param {Function} props.onPresetChange - Preset change handler
 * @param {Array} props.dateRange - Current date range [start, end]
 * @param {Function} props.onDateRangeChange - Date range change handler
 * @param {Function} props.onChange - Optional; when given it is called once
 *   with { preset, range } instead of onPresetChange + onDateRangeChange, so a
 *   page keeping filters in the URL can write both in a single update.
 * @param {React.ReactNode} props.children - Additional filter components
 * @param {Function} props.onClearFilters - Optional clear all filters handler
 * @param {string} props.title - Modal title (default: "Filters")
 */
const ReportFilterModal = ({
  visible,
  onClose,
  filterPreset,
  onPresetChange,
  dateRange,
  onDateRangeChange,
  onChange,
  children,
  onClearFilters,
  title = "Filters"
}) => {
  const isMobile = useIsMobile();

  const handlePresetClick = (preset) => {
    if (onChange) {
      onChange({ preset, range: getDateRangeFromPreset(preset) });
      return;
    }
    onPresetChange(preset);
    const range = getDateRangeFromPreset(preset);
    if (range) {
      onDateRangeChange(range);
    } else {
      onDateRangeChange(null);
    }
  };

  const handleCustomDateChange = (dates) => {
    const complete = Boolean(dates && dates[0] && dates[1]);
    if (onChange) {
      onChange(complete ? { preset: 'custom', range: [dates[0], dates[1]] } : { preset: 'all', range: null });
      return;
    }
    if (complete) {
      onDateRangeChange([dates[0], dates[1]]);
      onPresetChange('custom');
    } else {
      onDateRangeChange(null);
      onPresetChange('all');
    }
  };

  // Filter content - shared between Modal and Drawer
  const filterContent = (
    <>
      {/* Date Presets */}
      <div style={{ marginBottom: 16 }}>
        <Text strong style={{ display: 'block', marginBottom: 8 }}>Date Range</Text>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {DATE_PRESETS.map((preset) => (
            <Button
              key={preset.key}
              size="small"
              type={filterPreset === preset.key ? 'primary' : 'default'}
              onClick={() => handlePresetClick(preset.key)}
              style={{ marginBottom: 4 }}
            >
              {preset.label}
            </Button>
          ))}
        </div>
      </div>

      {/* Custom Date Range */}
      <div style={{ marginBottom: 16 }}>
        <Text strong style={{ display: 'block', marginBottom: 8 }}>Custom Date Range</Text>
        <RangePicker
          value={dateRange}
          onChange={handleCustomDateChange}
          format="YYYY-MM-DD"
          size="middle"
          allowClear
          placeholder={['Start Date', 'End Date']}
          style={{ width: '100%' }}
        />
      </div>

      {/* Additional Filters */}
      {children && (
        <>
          <Divider style={{ margin: '16px 0' }} />
          <div>
            <Text strong style={{ display: 'block', marginBottom: 12 }}>Additional Filters</Text>
            {children}
          </div>
        </>
      )}
    </>
  );

  // Footer content - shared between Modal and Drawer
  const footerContent = (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
      {onClearFilters && (
        <Button icon={<ClearOutlined />} onClick={onClearFilters} style={{ flex: 1 }}>
          Clear All
        </Button>
      )}
      <Button type="primary" onClick={onClose} style={{ flex: 1 }}>
        Apply Filters
      </Button>
    </div>
  );

  // Use Drawer for mobile - fully visible from bottom
  if (isMobile) {
    return (
      <Drawer
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <FilterOutlined />
            <span>{title}</span>
          </div>
        }
        placement="bottom"
        open={visible}
        onClose={onClose}
        height="90vh"
        styles={{
          body: {
            padding: '16px',
            paddingBottom: '80px',
            overflowY: 'auto'
          },
          footer: {
            padding: '12px 16px',
            borderTop: '1px solid #f0f0f0'
          }
        }}
        footer={footerContent}
      >
        {filterContent}
      </Drawer>
    );
  }

  // Use Modal for desktop
  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <FilterOutlined />
          <span>{title}</span>
        </div>
      }
      open={visible}
      onCancel={onClose}
      footer={footerContent}
      width={600}
      centered
    >
      {filterContent}
    </Modal>
  );
};

/**
 * DatePresetButtons - Inline date preset buttons for desktop
 */
export const DatePresetButtons = ({ filterPreset, onPresetChange, size = "small" }) => {
  return (
    <Space wrap size="small" style={{ maxWidth: '100%' }}>
      {DATE_PRESETS.map((preset) => (
        <Button
          key={preset.key}
          size={size}
          type={filterPreset === preset.key ? 'primary' : 'default'}
          onClick={() => onPresetChange(preset.key)}
        >
          {preset.label}
        </Button>
      ))}
    </Space>
  );
};

/**
 * MobileFilterButton - Button to open filter modal on mobile
 */
export const MobileFilterButton = ({ onClick, activeFiltersCount = 0 }) => {
  return (
    <Button
      icon={<FilterOutlined />}
      onClick={onClick}
      type={activeFiltersCount > 0 ? 'primary' : 'default'}
    >
      Filters {activeFiltersCount > 0 && `(${activeFiltersCount})`}
    </Button>
  );
};

export default ReportFilterModal;
